import { inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';

import { environment } from '../../../environments/environment';
import { UserEvent } from '../../shared/interfaces/user-event.interface';
import { AuthService } from './auth.service';
import { UserStore } from './user-store.service';

@Injectable({ providedIn: 'root' })
export class UserEventsService {
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly userStore = inject(UserStore);
  private readonly messages = inject(MessageService);

  private eventSource: EventSource | null = null;

  connect(): void {
    this.disconnect();

    const token = this.authService.getAccessToken();
    if (!token) return;

    const url = `${environment.apiUrl}/auth/events?token=${encodeURIComponent(token)}`;

    this.zone.runOutsideAngular(() => {
      this.eventSource = new EventSource(url);

      this.eventSource.onmessage = (event) => {
        this.zone.run(() => this.handleEvent(event));
      };

      this.eventSource.onerror = () => {
        if (this.eventSource?.readyState === EventSource.CLOSED) {
          this.disconnect();
        }
      };
    });
  }

  disconnect(): void {
    this.eventSource?.close();
    this.eventSource = null;
  }

  private handleEvent(event: MessageEvent): void {
    let parsed: UserEvent;
    try {
      parsed = JSON.parse(event.data) as UserEvent;
    } catch {
      return;
    }

    switch (parsed.type) {
      case 'access-changed':
        this.onAccessChanged(parsed);
        break;
      case 'session-revoked':
        this.onSessionRevoked();
        break;
    }
  }

  private onAccessChanged(event: UserEvent): void {
    if (!event.data) return;

    this.userStore.patchAccess(event.data);

    if (!this.userStore.hasAnyAccess()) {
      this.onSessionRevoked();
      return;
    }

    this.messages.add({
      severity: 'info',
      summary: 'Permisos actualizados',
      detail: 'Tus accesos fueron modificados por un administrador.',
      life: 5000,
    });
  }

  private onSessionRevoked(): void {
    this.disconnect();
    this.authService.logout();
    this.router.navigateByUrl('/login');
    this.messages.add({
      severity: 'warn',
      summary: 'Sesión finalizada',
      detail: 'Tu acceso fue revocado por un administrador.',
      life: 8000,
    });
  }
}
